<template>
  <CCard class="crm-reports mt-4">
    <CCardHeader class="d-flex align-items-center">
      <div>
        <CIcon name="cil-notes" />
        <strong class="ml-2">{{ title }}</strong>
      </div>

      <CButton
        v-if="allowCreate"
        color="primary"
        size="sm"
        class="ml-auto"
        @click="toggleForm"
      >
        <CIcon :name="showForm ? 'cil-x-circle' : 'cil-task'" />
        <span class="ml-1">{{ showForm ? 'Cancel' : 'Add Report' }}</span>
      </CButton>
    </CCardHeader>

    <CCardBody>
      <CRow
        v-if="showLinkFilter || showUserFilter || showOutcomeFilter"
        class="mb-3"
      >
        <CCol v-if="showLinkFilter" md="4">
          <div class="form-group mb-0">
            <label>Report filter</label>
            <div class="btn-group btn-group-sm d-flex report-link-filter">
              <CButton
                v-for="option in reportLinkOptions"
                :key="option.value"
                type="button"
                class="flex-fill"
                :color="reportLinkFilter === option.value ? 'primary' : 'secondary'"
                :variant="reportLinkFilter === option.value ? '' : 'outline'"
                @click="reportLinkFilter = option.value"
              >
                {{ option.label }}
              </CButton>
            </div>
          </div>
        </CCol>

        <CCol v-if="showUserFilter" md="4">
          <div class="form-group mb-0">
            <label>User filter</label>
            <v-select
              label="label"
              placeholder="All users"
              v-model="selectedUserId"
              :options="userOptions"
              :reduce="user => user.id"
              :loading="loadingUsers"
              clearable
            />
          </div>
        </CCol>

        <CCol v-if="showOutcomeFilter" md="4">
          <div class="form-group mb-0">
            <label>Outcome filter</label>
            <v-select
              label="name"
              placeholder="All outcomes"
              v-model="selectedOutcomeId"
              :options="outcomeOptions"
              :reduce="outcome => outcome.id"
              :loading="loadingOutcomes"
              clearable
            />
          </div>
        </CCol>
      </CRow>

      <CForm v-if="showForm" class="mb-4" @submit.prevent="submitReport">
        <CAlert v-if="taskId" color="light" class="border">
          <CIcon name="cil-bolt" class="mr-1" />
          <strong>Task context:</strong>
          <span class="ml-1">{{ contextLabel }}</span>
        </CAlert>

        <CAlert v-else-if="fixedContextLabel" color="light" class="border">
          <CIcon :name="restaurantId ? 'cil-restaurant' : 'cil-location-pin'" class="mr-1" />
          <strong>Report context:</strong>
          <span class="ml-1">{{ fixedContextLabel }}</span>
        </CAlert>

        <div v-if="showContextSelector" class="form-group">
          <label>Report context</label>
          <div class="btn-group btn-group-sm d-block mb-2">
            <CButton
              type="button"
              :color="contextType === 'restaurant' ? 'primary' : 'secondary'"
              :variant="contextType === 'restaurant' ? '' : 'outline'"
              @click="contextType = 'restaurant'"
            >
              Restaurant
            </CButton>
            <CButton
              type="button"
              :color="contextType === 'prospect' ? 'primary' : 'secondary'"
              :variant="contextType === 'prospect' ? '' : 'outline'"
              @click="contextType = 'prospect'"
            >
              Prospect
            </CButton>
          </div>

          <v-select
            v-if="contextType === 'restaurant'"
            label="label"
            placeholder="Link to restaurant..."
            v-model="form.restaurant_id"
            :options="restaurants"
            :reduce="restaurant => restaurant.value"
            :loading="loadingRestaurants"
            clearable
          />

          <CrmProspectSelector
            v-else
            v-model="form.crm_prospect_id"
            @selected="selectedProspect = $event"
          />
        </div>

        <CRow>
          <CCol md="4">
            <CSelect
              label="Type"
              :value.sync="form.report_type"
              :options="reportTypeOptions"
            />
          </CCol>

          <CCol md="4">
            <div class="form-group">
              <label>Outcome</label>
              <v-select
                label="name"
                placeholder="Select outcome..."
                v-model="form.outcome_id"
                :options="outcomeOptions"
                :reduce="outcome => outcome.id"
                :loading="loadingOutcomes"
                clearable
              />

              <div v-if="canManageOutcomes" class="input-group input-group-sm mt-2">
                <input
                  class="form-control"
                  placeholder="New outcome"
                  v-model.trim="newOutcomeName"
                  @keyup.enter.prevent="storeOutcome"
                />
                <div class="input-group-append">
                  <CButton
                    type="button"
                    color="secondary"
                    variant="outline"
                    size="sm"
                    :disabled="savingOutcome || !newOutcomeName"
                    @click="storeOutcome"
                  >
                    <CSpinner v-if="savingOutcome" size="sm" />
                    <CIcon v-else name="cil-plus" />
                  </CButton>
                </div>
              </div>
            </div>
          </CCol>

          <CCol md="4">
            <CInput
              type="datetime-local"
              label="Next follow-up"
              :value.sync="form.next_follow_up_at"
            />
          </CCol>
        </CRow>

        <CTextarea
          label="Notes"
          placeholder="Write the report notes"
          rows="4"
          :value.sync="form.notes"
        />

        <CButton
          type="submit"
          color="primary"
          :disabled="saving || !canSubmit"
        >
          <CSpinner v-if="saving" size="sm" class="mr-1" />
          <CIcon v-else name="cil-save" />
          <span class="ml-1">{{ editingReport ? 'Update Report' : 'Save Report' }}</span>
        </CButton>
      </CForm>

      <CElementCover :opacity="0.25" v-show="loading" />

      <div v-if="reports.length" class="crm-report-list">
        <div
          v-for="group in groupedReports"
          :key="group.key"
          class="crm-report-day"
        >
          <div
            v-if="groupByDay"
            class="crm-report-day-header d-flex flex-wrap align-items-center justify-content-between"
          >
            <div class="d-flex flex-wrap align-items-center">
              <CIcon name="cil-calendar" class="mr-2" />
              <strong>{{ group.title }}</strong>
              <span class="small text-muted ml-2">{{ group.dateLabel }}</span>
            </div>
            <CBadge color="light">
              {{ group.reports.length }} {{ group.reports.length === 1 ? 'report' : 'reports' }}
            </CBadge>
          </div>

          <div
            v-for="report in group.reports"
            :key="report.id"
            class="crm-report-item py-3"
          >
            <div class="d-flex align-items-start">
              <div class="flex-grow-1">
                <div class="d-flex flex-wrap align-items-center mb-1">
                  <CBadge color="info" class="mr-2">{{ reportTypeLabel(report.report_type) }}</CBadge>
                  <strong>{{ outcomeLabel(report) }}</strong>
                </div>

                <div class="small text-muted">
                  <CIcon name="cil-user" class="mr-1" />
                  {{ submittedByName(report) }}
                  <span class="mx-2">|</span>
                  <CIcon name="cil-calendar" class="mr-1" />
                  {{ moment(report.created_at).format('LT') }}
                </div>

                <div v-if="report.restaurant || report.prospect || report.task" class="small text-muted mt-1">
                  <template v-if="report.restaurant">
                    <CIcon name="cil-restaurant" class="mr-1" />
                    <CLink :to="{ name: 'View Restaurant', params: { id: report.restaurant_id }}">
                      {{ report.restaurant.restaurant_name }}
                    </CLink>
                  </template>

                  <template v-if="report.prospect">
                    <span v-if="report.restaurant" class="mx-2">|</span>
                    <CIcon name="cil-location-pin" class="mr-1" />
                    {{ prospectLabel(report.prospect) }}
                  </template>

                  <template v-if="report.task">
                    <span v-if="report.restaurant || report.prospect" class="mx-2">|</span>
                    <CIcon name="cil-bolt" class="mr-1" />
                    <CLink :to="{ name: 'Message', params: { id: report.task.task_id, folder: 'inbox' }, query: { user: report.task.user_id } }">
                      {{ report.task.subject || report.task.task_id }}
                    </CLink>
                  </template>
                </div>

                <div v-if="report.next_follow_up_at" class="small text-muted mt-1">
                  <CIcon name="cil-calendar" class="mr-1" />
                  Follow-up {{ moment(report.next_follow_up_at).calendar() }}
                </div>

                <p v-if="report.notes" class="mb-0 mt-2 report-notes">{{ report.notes }}</p>
              </div>

              <div v-if="report.is_editable || report.is_deletable" class="btn-group ml-2">
                <CButton
                  v-if="report.is_editable"
                  size="sm"
                  color="info"
                  variant="ghost"
                  v-c-tooltip="{ content: 'Edit report' }"
                  @click="editReport(report)"
                >
                  <CIcon name="cil-pencil" />
                </CButton>

                <CButton
                  v-if="report.is_deletable"
                  size="sm"
                  color="danger"
                  variant="ghost"
                  v-c-tooltip="{ content: 'Delete report' }"
                  @click="deleteReport(report)"
                >
                  <CIcon name="cil-trash" />
                </CButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="!loading" class="text-muted small">
        No CRM reports yet.
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import CrmProspectSelector from "@/views/crm/prospects/CrmProspectSelector";

export default {
  name: "CrmReports",
  components: {
    CrmProspectSelector,
  },
  props: {
    title: {
      type: String,
      default: 'CRM Reports',
    },
    taskId: {
      type: String,
      default: null,
    },
    restaurantId: {
      type: [Number, String],
      default: null,
    },
    prospectId: {
      type: [Number, String],
      default: null,
    },
    prospect: {
      type: Object,
      default: null,
    },
    standalone: {
      type: Boolean,
      default: false,
    },
    groupByDay: {
      type: Boolean,
      default: false,
    },
    unlinkedOnly: {
      type: Boolean,
      default: false,
    },
    showLinkFilter: {
      type: Boolean,
      default: false,
    },
    showUserFilter: {
      type: Boolean,
      default: false,
    },
    showOutcomeFilter: {
      type: Boolean,
      default: true,
    },
    allowCreate: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: false,
      loadingOutcomes: false,
      loadingRestaurants: false,
      loadingUsers: false,
      saving: false,
      savingOutcome: false,
      showForm: false,
      editingReport: null,
      selectedOutcomeId: null,
      selectedUserId: null,
      selectedProspect: null,
      reportLinkFilter: this.unlinkedOnly ? 'standalone' : 'all',
      reports: [],
      restaurants: [],
      userOptions: [],
      outcomeOptions: [],
      newOutcomeName: '',
      contextType: 'restaurant',
      reportTypeOptions: [
        { label: 'General', value: 'general' },
        { label: 'Restaurant visit', value: 'visit' },
        { label: 'Call', value: 'call' },
        { label: 'Follow-up', value: 'follow_up' },
        { label: 'Email', value: 'email' },
      ],
      reportLinkOptions: [
        { label: 'All', value: 'all' },
        { label: 'Standalone', value: 'standalone' },
        { label: 'Task-linked', value: 'task_linked' },
      ],
      form: this.emptyForm(),
    };
  },
  computed: {
    showContextSelector() {
      return !this.taskId && !this.restaurantId && !this.prospectId;
    },
    fixedContextLabel() {
      if (this.restaurantId) return `Restaurant #${this.restaurantId}`;
      if (this.prospectId || this.prospect) return this.prospectLabel(this.prospect) || `Prospect #${this.prospectId}`;

      return '';
    },
    contextLabel() {
      if (this.restaurantId) return `Restaurant #${this.restaurantId}`;
      if (this.prospectId || this.prospect) return this.prospectLabel(this.prospect) || `Prospect #${this.prospectId}`;

      return 'This task has no linked restaurant or prospect.';
    },
    canManageOutcomes() {
      return ['SuperAdmin', 'Admin'].some((role) => this.authUserRoles.includes(role));
    },
    hasReportContent() {
      return !!(
        this.form.notes ||
        this.form.outcome_id ||
        this.form.outcome ||
        this.form.next_follow_up_at
      );
    },
    hasReportContext() {
      if (this.taskId || this.restaurantId || this.prospectId) return true;

      return this.contextType === 'restaurant'
        ? !!this.form.restaurant_id
        : !!this.form.crm_prospect_id;
    },
    canSubmit() {
      return this.hasReportContent && this.hasReportContext;
    },
    groupedReports() {
      if (!this.groupByDay) {
        return [{
          key: 'all',
          reports: this.reports,
        }];
      }

      const groups = [];
      const groupsByDate = {};

      this.reports.forEach((report) => {
        const date = this.moment(report.created_at);
        const isValidDate = date.isValid();
        const key = isValidDate ? date.format('YYYY-MM-DD') : 'unknown';

        if (!groupsByDate[key]) {
          const group = {
            key,
            title: isValidDate ? this.reportDayTitle(date) : 'Unknown date',
            dateLabel: isValidDate ? date.format('dddd, D MMMM YYYY') : '',
            reports: [],
          };

          groupsByDate[key] = group;
          groups.push(group);
        }

        groupsByDate[key].reports.push(report);
      });

      return groups;
    },
    effectiveUnlinkedOnly() {
      if (!this.showLinkFilter) return this.unlinkedOnly;

      return this.reportLinkFilter === 'standalone';
    },
    taskLinkedOnly() {
      return this.showLinkFilter && this.reportLinkFilter === 'task_linked';
    },
    reloadKey() {
      return [
        this.taskId || '',
        this.restaurantId || '',
        this.prospectId || '',
        this.standalone ? 'standalone' : '',
        this.effectiveUnlinkedOnly ? 'unlinked' : '',
        this.taskLinkedOnly ? 'task-linked' : '',
        this.selectedOutcomeId || '',
        this.selectedUserId || '',
      ].join('|');
    },
  },
  created() {
    this.getOutcomes();
    if (this.showUserFilter) {
      this.getUsers();
    }
  },
  watch: {
    reloadKey: {
      immediate: true,
      async handler() {
        await this.getReports();
      },
    },
    showContextSelector: {
      immediate: true,
      async handler(value) {
        if (value) {
          await this.getRestaurants();
        }
      },
    },
    contextType(value) {
      if (value === 'restaurant') {
        this.form.crm_prospect_id = null;
        this.selectedProspect = null;
      } else {
        this.form.restaurant_id = null;
      }
    },
  },
  methods: {
    emptyForm() {
      return {
        restaurant_id: null,
        crm_prospect_id: null,
        report_type: 'general',
        outcome_id: null,
        outcome: '',
        notes: '',
        next_follow_up_at: null,
      };
    },
    async getReports() {
      this.loading = true;

      const params = {
        'outcome-id': this.selectedOutcomeId || null,
        'submitted-by-user-id': this.selectedUserId || null,
      };

      const request = this.taskId
        ? this.$axios.get(
          this.$backend.CRM_REPORTS.TASK_GET_ALL.replace('{task}', this.taskId),
          { params }
        )
        : this.$axios.get(this.$backend.CRM_REPORTS.GET_ALL, {
          params: {
            ...params,
            'restaurant-id': this.restaurantId || null,
            'crm-prospect-id': this.prospectId || null,
            unlinked: this.effectiveUnlinkedOnly ? 1 : null,
          },
        });

      await request
        .then((response) => {
          const reports = response.data.data || response.data;
          this.reports = this.taskLinkedOnly && Array.isArray(reports)
            ? reports.filter(this.isTaskLinkedReport)
            : reports;
        })
        .catch((error) => {
          this.$toast.error(error.response?.data?.message || 'CRM reports could not be loaded.');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async getOutcomes() {
      this.loadingOutcomes = true;

      await this.$axios
        .get(this.$backend.CRM_REPORTS.OUTCOMES)
        .then((response) => {
          this.outcomeOptions = response.data || [];
        })
        .catch(() => {
          this.outcomeOptions = [];
        })
        .finally(() => {
          this.loadingOutcomes = false;
        });
    },
    async getUsers() {
      this.loadingUsers = true;

      await this.$axios
        .get(this.$backend.CRM_REPORTS.USERS)
        .then((response) => {
          this.userOptions = (response.data || []).map((user) => {
            const name = [user.first_name, user.last_name].filter(Boolean).join(' ');

            return {
              ...user,
              label: name && user.email ? `${name} (${user.email})` : name || user.email || `User #${user.id}`,
            };
          });

          if (
            this.selectedUserId
            && !this.userOptions.some((user) => `${user.id}` === `${this.selectedUserId}`)
          ) {
            this.selectedUserId = null;
          }
        })
        .catch(() => {
          this.userOptions = [];
        })
        .finally(() => {
          this.loadingUsers = false;
        });
    },
    async storeOutcome() {
      if (!this.newOutcomeName || this.savingOutcome) return;

      this.savingOutcome = true;
      await this.$axios
        .post(this.$backend.CRM_REPORTS.STORE_OUTCOME, { name: this.newOutcomeName })
        .then((response) => {
          const outcome = response.data.data;
          const exists = this.outcomeOptions.some((item) => `${item.id}` === `${outcome.id}`);

          if (!exists) {
            this.outcomeOptions.push(outcome);
            this.outcomeOptions.sort((a, b) => a.name.localeCompare(b.name));
          }

          this.form.outcome_id = outcome.id;
          this.form.outcome = outcome.name;
          this.newOutcomeName = '';
          this.$toast.success(response.data.message);
        })
        .catch((error) => {
          this.$toast.error(error.response?.data?.message || 'CRM report outcome could not be saved.');
        })
        .finally(() => {
          this.savingOutcome = false;
        });
    },
    async getRestaurants() {
      if (this.restaurants.length || this.loadingRestaurants) return;

      this.loadingRestaurants = true;
      await this.$axios
        .get(this.$backend.RESTAURANT.GET_ALL_NAME)
        .then((response) => {
          this.restaurants = response.data;
        })
        .catch(() => {
          this.restaurants = [];
        })
        .finally(() => {
          this.loadingRestaurants = false;
        });
    },
    toggleForm() {
      this.showForm = !this.showForm;
      if (!this.showForm) {
        this.resetForm();
      }
    },
    resetForm() {
      this.editingReport = null;
      this.form = this.emptyForm();
      this.contextType = 'restaurant';
      this.selectedProspect = null;
    },
    editReport(report) {
      this.editingReport = report;
      this.contextType = report.crm_prospect_id ? 'prospect' : 'restaurant';
      this.selectedProspect = report.prospect || null;
      this.form = {
        restaurant_id: report.restaurant_id || null,
        crm_prospect_id: report.crm_prospect_id || null,
        report_type: report.report_type || 'general',
        outcome_id: report.outcome_id || this.findOutcomeId(report.outcome),
        outcome: report.outcome || '',
        notes: report.notes || '',
        next_follow_up_at: this.toDatetimeLocalValue(report.next_follow_up_at),
      };
      this.showForm = true;
    },
    async submitReport() {
      if (!this.canSubmit) return;

      const outcome = this.selectedOutcome();
      const payload = {
        report_type: this.form.report_type,
        outcome_id: this.form.outcome_id || null,
        outcome: outcome ? outcome.name : this.form.outcome || null,
        notes: this.form.notes || null,
        next_follow_up_at: this.form.next_follow_up_at || null,
      };

      if (!this.taskId) {
        if (this.restaurantId) {
          payload.restaurant_id = this.restaurantId;
          payload.crm_prospect_id = null;
        } else if (this.prospectId) {
          payload.restaurant_id = null;
          payload.crm_prospect_id = this.prospectId;
        } else if (this.contextType === 'restaurant') {
          payload.restaurant_id = this.form.restaurant_id || null;
          payload.crm_prospect_id = null;
        } else {
          payload.restaurant_id = null;
          payload.crm_prospect_id = this.form.crm_prospect_id || null;
        }
      }

      const request = this.editingReport
        ? this.$axios.put(
          this.$backend.CRM_REPORTS.UPDATE.replace('{id}', this.editingReport.id),
          payload
        )
        : this.taskId
          ? this.$axios.post(
            this.$backend.CRM_REPORTS.TASK_STORE.replace('{task}', this.taskId),
            payload
          )
          : this.$axios.post(this.$backend.CRM_REPORTS.STORE, payload);

      this.saving = true;
      await request
        .then((response) => {
          this.$toast.success(response.data.message);
          this.resetForm();
          this.showForm = false;
          return Promise.all([
            this.getReports(),
            this.showUserFilter ? this.getUsers() : Promise.resolve(),
          ]);
        })
        .catch((error) => {
          this.$toast.error(error.response?.data?.message || 'CRM report could not be saved.');
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async deleteReport(report) {
      const confirm = await this.mSwal.fire({
        title: 'Delete CRM report?',
        text: 'This report will be removed.',
        icon: 'warning',
        showCancelButton: true,
      });

      if (!confirm.isConfirmed) return;

      await this.$axios
        .delete(this.$backend.CRM_REPORTS.DELETE.replace('{id}', report.id))
        .then(async (response) => {
          this.$toast.success(response.data.message);
          this.reports = this.reports.filter((item) => item.id !== report.id);

          if (this.showUserFilter) {
            await this.getUsers();
          }
        })
        .catch((error) => {
          this.$toast.error(error.response?.data?.message || 'CRM report could not be deleted.');
        });
    },
    selectedOutcome() {
      return this.outcomeOptions.find((item) => `${item.id}` === `${this.form.outcome_id}`);
    },
    findOutcomeId(name) {
      if (!name) return null;

      const outcome = this.outcomeOptions.find(
        (item) => item.name.toLowerCase() === name.toLowerCase()
      );

      return outcome ? outcome.id : null;
    },
    outcomeLabel(report) {
      return report.outcome_option?.name || report.outcome || report.prospect?.name || 'No outcome';
    },
    submittedByName(report) {
      const user = report.submitted_by;
      if (!user) return `User #${report.submitted_by_user_id}`;

      return [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
    },
    prospectLabel(prospect) {
      if (!prospect) return '';

      return [
        prospect.name,
        prospect.address,
        [prospect.postcode, prospect.city].filter(Boolean).join(' '),
      ].filter(Boolean).join(', ');
    },
    reportTypeLabel(value) {
      const option = this.reportTypeOptions.find((item) => item.value === value);

      return option ? option.label : value;
    },
    reportDayTitle(date) {
      if (date.isSame(this.moment(), 'day')) return 'Today';
      if (date.isSame(this.moment().subtract(1, 'day'), 'day')) return 'Yesterday';

      return date.format('dddd');
    },
    isTaskLinkedReport(report) {
      return !!(report.crm_task_id || report.task_id || report.task);
    },
    toDatetimeLocalValue(value) {
      if (!value) return null;

      return this.moment(value).format('YYYY-MM-DDTHH:mm');
    },
  },
};
</script>

<style scoped>
.crm-report-day + .crm-report-day {
  margin-top: 1rem;
}

.crm-report-day-header {
  padding: 0.5rem 0.75rem;
  border-left: 3px solid #3399ff;
  border-radius: 0.25rem;
  background: #f0f3f5;
}

.crm-report-list .crm-report-item {
  border-top: 1px solid #d8dbe0;
}

.crm-report-list .crm-report-item:first-child {
  border-top: 0;
  padding-top: 0 !important;
}

.report-notes {
  white-space: pre-line;
}

.report-link-filter .btn {
  min-width: 7rem;
}
</style>
